import React, { useEffect, useState } from 'react';
import UserCard from '../components/mobile/BranchUser';
import BranchUserHeader from '../components/common/BranchUser';
import BranchUserForm from '../components/form/BranchUser';
import UserTable from '../components/desktop/BranchUser';
import Pagination from '../components/Pagination';
import Spinner from '../components/ui/Spinner';
import { getBranches } from '../services/branches';
import { createBranchUser, getBranchUsers, toggleBranchUserStatus, updateBranchUsersPartial } from '../services/branch_users';
import { toast } from 'react-toastify';
import { USER_ROLE } from '../services/auth';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const BranchUsers = ({ restrictToBranchUsersOnly = false }) => {
  const [branchUsers, setBranchUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState([]);
  const [count, setCount] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('Active');
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });
  const usersPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [filter, search, restrictToBranchUsersOnly]);

  useEffect(() => {
    // Reset form state when switching between branch admin and branch user modes.
    setShowForm(false);
    setEditingUser(null);
    setErrors([]);
  }, [restrictToBranchUsersOnly]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchBranchUsers(pendingPage);
    }
  }, [pendingPage]);

  useEffect(() => {
    const user = localStorage.getItem(USER_ROLE);
    if (user == 'admin' && branches.length == 0) {
      fetchBranches();
    }
  }, []);

  const fetchBranchUsers = async (page) => {
    setLoading(true);
    let params = {
      page, search,
      status:filter.toLowerCase(),
      pageSize:usersPerPage
    };
    if (!restrictToBranchUsersOnly) {
      params.user_type = 'branch_admin';
    }
    await getBranchUsers(params).then(([err, data]) => {
      const rows = Array.isArray(data?.results) ? data.results : [];
      const normalizedFilter = (filter || 'all').toLowerCase();
      const filteredRows = normalizedFilter === 'active'
        ? rows.filter((item) => Boolean(item?.is_active))
        : normalizedFilter === 'inactive'
        ? rows.filter((item) => !Boolean(item?.is_active))
        : rows;
      setBranchUsers(filteredRows);
      setCount(data.count);
      setCurrentPage(page);
      setPendingPage(null);
      setLoading(false);
    })
    .catch((error) => {
      setPendingPage(null);
      setLoading(false);
    });
  };

  const fetchBranches = async () => {
    setLoading(true);
    await getBranches({ paginate: false })
      .then(([err, data]) => {
        setBranches(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingUser(null);
    setShowForm(true);
    setErrors([]);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
    setErrors([]);
  };

  const handleSave = async (userData) => {
    let err, response;
    setLoading(true);
    if (editingUser) {
      [err, response] = await updateBranchUsersPartial(userData.id, userData);
    } else {
      [err, response] = await createBranchUser(userData);
    }
    setLoading(false);
    if (err) {
      setErrors(err);
    } else {
      await fetchBranchUsers(currentPage);
      toast.success("Branch User Saved Successfully.");
      setShowForm(false);
    }
  };

  const handleToggleStatus = async (user) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: user.is_active ? 'Deactivate User' : 'Activate User',
      message: `Are you sure you want to ${user.is_active ? 'deactivate' : 'activate'} the user "${user.name}"?`,
      onConfirm: () => handleAction(user),
    });
  };

  const handleAction = async (user) => {
    setLoading(true);
    await toggleBranchUserStatus(user.id, !user.is_active);
    setConfirmModalParams({...confirmModalParams, open: false});
    await fetchBranchUsers(currentPage);
    setLoading(false);
  };

  const onFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputSearch.trim());
  };

  const onSearchTextChange = (e) => {
    setInputSearch(e.target.value)
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const handleBranchUserSelection = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  return (
    <div className="relative p-4 w-full">
      <BranchUserHeader
        count={count}
        filter={filter}
        inputSearch={inputSearch}
        onFilterChange={onFilterChange}
        onSearchSubmit={onSearchSubmit}
        onSearchTextChange={onSearchTextChange}
        onClearSearch={onClearSearch}
        handleAdd={handleAdd}
        user={editingUser}
        showForm={showForm}
      />
      {showForm ? (
        <BranchUserForm
          user={editingUser}
          branches={branches}
          dataErrors={errors}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          restrictToBranchUsersOnly={restrictToBranchUsersOnly}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
              <UserCard
                users={branchUsers}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
              />
              <UserTable
                users={branchUsers}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
                onBranchUserSelected={handleBranchUserSelection}
              />
            </>
          )}

          {/* ✅ Pagination only if users exist */}
          <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
            {branchUsers.length > 0 && (
                <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(count / usersPerPage))}
                onPageChange={setPendingPage}
                />
            )}
          </div>
        </>
      )}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({...confirmModalParams, open: false})}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default BranchUsers;
